import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, User, DollarSign, TrendingUp, Briefcase, Activity } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const AdminViewAsUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserView();
    fetchChartData();
  }, [userId]);

  const fetchUserView = async () => {
    try {
      const response = await api.get(`/admin/view-as-user/${userId}`);
      setUserData(response.data.user);
      setDashboardData(response.data.dashboard);
    } catch (error) {
      toast.error('Failed to load user view');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      // For now, we'll use the general chart endpoint
      // In production, you'd want a user-specific chart endpoint
      const response = await api.get('/dashboard/monthly-net-rtr-chart');
      setChartData(response.data);
    } catch (error) {
      console.error('Failed to load chart data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading user view...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Users</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">View as User</h1>
            <p className="text-slate-600 mt-1">
              Viewing dashboard as: <span className="font-semibold">{userData?.first_name} {userData?.last_name}</span>
            </p>
          </div>
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <User className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{userData?.first_name} {userData?.last_name}</h2>
            <p className="text-blue-100">{userData?.email}</p>
          </div>
        </div>
      </div>

      {/* Dashboard KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Estimated Account Value</p>
              <p className="text-3xl font-bold text-slate-900">
                ${dashboardData?.account_value?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">All Time Gain</p>
              <p className="text-3xl font-bold text-green-600">
                +${dashboardData?.all_time_gain?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Total Investments</p>
              <p className="text-3xl font-bold text-slate-900">
                {dashboardData?.total_investments || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Active Investments</p>
              <p className="text-3xl font-bold text-slate-900">
                {dashboardData?.active_investments || 0}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Activity className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-slate-900">Recent Activity</h2>
        </div>
        <div className="p-6">
          {dashboardData?.recent_transactions?.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.recent_transactions.map((transaction, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div>
                    <p className="font-medium text-slate-900">{transaction.type}</p>
                    <p className="text-sm text-slate-600">
                      {new Date(transaction.date).toLocaleDateString()}
                    </p>
                  </div>
                  <p className={`font-semibold ${
                    transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ${Math.abs(transaction.amount).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No recent transactions</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> This is a read-only view of the user's dashboard. 
          You are seeing the data as the investor would see it on their portal.
        </p>
      </div>
    </div>
  );
};

export default AdminViewAsUser;
